import { useEffect, useState } from 'react';
import axios from 'axios';
import { BookOpen, Plus, Edit, Trash2, Loader2, X, Save, Youtube } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = ['Engineering', 'JEE', 'NEET'];

export default function AdminCourses() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    category: 'Engineering',
    description: '',
    duration: '',
    price: '',
    image_url: '',
    youtube_intro_url: '',
    features: ['']
  });

  useEffect(() => {
    fetchCourses();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API}/admin/courses`, getAuthHeaders());
      setCourses(response.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
      toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (courseId) => {
    if (!window.confirm('Are you sure you want to delete this course?')) return;

    try {
      await axios.delete(`${API}/admin/courses/${courseId}`, getAuthHeaders());
      toast.success('Course deleted successfully');
      fetchCourses();
    } catch (error) {
      console.error('Error deleting course:', error);
      toast.error('Failed to delete course');
    }
  };

  const openCreateModal = () => {
    setEditingCourse(null);
    setFormData({
      name: '',
      category: 'Engineering',
      description: '',
      duration: '',
      price: '',
      image_url: '',
      youtube_intro_url: '',
      features: ['']
    });
    setShowModal(true);
  };

  const openEditModal = (course) => {
    setEditingCourse(course);
    setFormData({
      name: course.name,
      category: course.category,
      description: course.description,
      duration: course.duration,
      price: course.price,
      image_url: course.image_url || '',
      youtube_intro_url: course.youtube_intro_url || '',
      features: course.features?.length > 0 ? course.features : ['']
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.description.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        features: formData.features.filter(f => f.trim())
      };

      if (editingCourse) {
        await axios.put(`${API}/admin/courses/${editingCourse.course_id}`, payload, getAuthHeaders());
        toast.success('Course updated successfully');
      } else {
        await axios.post(`${API}/admin/courses`, payload, getAuthHeaders());
        toast.success('Course created successfully');
      }

      setShowModal(false);
      fetchCourses();
    } catch (error) {
      console.error('Error saving course:', error);
      toast.error('Failed to save course');
    } finally {
      setSaving(false);
    }
  };

  const addFeature = () => {
    setFormData(prev => ({
      ...prev,
      features: [...prev.features, '']
    }));
  };

  const updateFeature = (index, value) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.map((f, i) => i === index ? value : f)
    }));
  };

  const removeFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-brand-dark font-display">Manage Courses</h1>
          <p className="text-slate-600 mt-2">Total Courses: {courses.length}</p>
        </div>
        <Button data-testid="create-course-btn" onClick={openCreateModal} className="btn-primary">
          <Plus className="h-5 w-5 mr-2" />
          Add Course
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="inline-block h-8 w-8 animate-spin text-brand-blue" />
          <p className="mt-4 text-slate-600">Loading courses...</p>
        </div>
      ) : courses.length === 0 ? (
        <div className="text-center py-12">
          <BookOpen className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-600 mb-4">No courses created yet.</p>
          <Button onClick={openCreateModal} className="btn-primary">
            <Plus className="h-5 w-5 mr-2" />
            Create Your First Course
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses.map((course) => (
            <div key={course.course_id} className="border border-slate-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
              {course.image_url && (
                <img src={course.image_url} alt={course.name} className="w-full h-40 object-cover" />
              )}
              <div className="p-4">
                <span className={`text-xs font-semibold px-2 py-1 rounded ${
                  course.category === 'JEE' ? 'bg-orange-100 text-orange-700' :
                  course.category === 'NEET' ? 'bg-green-100 text-green-700' :
                  'bg-blue-100 text-blue-700'
                }`}>
                  {course.category}
                </span>
                <h3 className="text-lg font-semibold text-brand-dark mt-2">{course.name}</h3>
                <p className="text-sm text-slate-600 mt-1 line-clamp-2">{course.description}</p>
                <div className="flex justify-between items-center mt-3 text-sm text-slate-500">
                  <span>{course.duration}</span>
                  <span className="font-semibold text-brand-blue">{course.price}</span>
                </div>
                {course.youtube_intro_url && (
                  <div className="mt-2">
                    <Youtube className="inline h-4 w-4 text-red-500 mr-1" />
                    <span className="text-xs text-slate-500">Intro Video</span>
                  </div>
                )}
                <div className="flex gap-2 mt-4">
                  <Button onClick={() => openEditModal(course)} variant="outline" size="sm" className="flex-1">
                    <Edit className="h-4 w-4 mr-1" /> Edit
                  </Button>
                  <Button onClick={() => handleDelete(course.course_id)} variant="destructive" size="sm">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-slate-200 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-brand-dark">
                {editingCourse ? 'Edit Course' : 'Create New Course'}
              </h2>
              <Button onClick={() => setShowModal(false)} variant="ghost" size="sm">
                <X className="h-5 w-5" />
              </Button>
            </div>

            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Course Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., JEE Physics Complete"
                  />
                </div>
                <div>
                  <Label>Category *</Label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full h-10 px-3 border border-slate-200 rounded-md"
                  >
                    {CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <Label>Description *</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Course description..."
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Duration</Label>
                  <Input
                    value={formData.duration}
                    onChange={(e) => setFormData(prev => ({ ...prev, duration: e.target.value }))}
                    placeholder="e.g., 3 Months"
                  />
                </div>
                <div>
                  <Label>Price</Label>
                  <Input
                    value={formData.price}
                    onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                    placeholder="e.g., ₹5000"
                  />
                </div>
              </div>

              <div>
                <Label>Image URL</Label>
                <Input
                  value={formData.image_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                  placeholder="https://..."
                />
              </div>

              <div>
                <Label>YouTube Intro Video URL</Label>
                <Input
                  value={formData.youtube_intro_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, youtube_intro_url: e.target.value }))}
                  placeholder="https://youtube.com/watch?v=..."
                />
              </div>

              <div>
                <Label>Features</Label>
                {formData.features.map((feature, idx) => (
                  <div key={idx} className="flex gap-2 mt-2">
                    <Input
                      value={feature}
                      onChange={(e) => updateFeature(idx, e.target.value)}
                      placeholder="e.g., Live doubt sessions"
                    />
                    <Button type="button" onClick={() => removeFeature(idx)} variant="ghost" size="sm">
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button type="button" onClick={addFeature} variant="outline" size="sm" className="mt-2">
                  <Plus className="h-4 w-4 mr-1" /> Add Feature
                </Button>
              </div>
            </form>

            <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
              <Button type="button" onClick={() => setShowModal(false)} variant="outline">Cancel</Button>
              <Button onClick={handleSubmit} className="btn-primary" disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                {editingCourse ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
